import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  IconButton,
  alpha,
  useTheme,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Feedback as FeedbackIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAppContext } from './GrowthSessionManagement';
import { createGrowthSession as createGrowthSessionAPI, updateGrowthSession as updateGrowthSessionAPI } from '../Queries';

// Helper function to format date strings
const formatDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().split('T')[0];
};

const GrowthSessionDialog = ({ open, onClose, editingGrowthSession, selectedUserId, onSuccess, forceNew = false }) => {
  const { addGrowthSession, updateGrowthSession, getUsersByRole, growthSessions } = useAppContext();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calculate previous week dates
  const today = new Date();
  const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);

  const initialFormState = {
    user_id: '',
    lead_id: '',
    manager_id: '',
    date: formatDate(new Date()),
    review_period_from: formatDate(twoWeeksAgo),
    review_period_to: formatDate(oneWeekAgo),
    status: 'draft',
    session_data: {
      key_strengths: '',
      areas_for_improvement: '',
      recommended_action_plan: '',
      support_required: '',
      suggested_plan_of_action: '',
      dynamic_fields: []
    },
    manager_comments: ''
  };

  const [growthSessionForm, setGrowthSessionForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  
  // Determine if we should show accordion (edit mode) or timeline (create mode)
  const isEditMode = Boolean(editingGrowthSession);
  const showAccordions = isEditMode;

  const steps = [
    'Basic Information',
    'Feedback Details',
    'Review & Finalize'
  ];

  const usersList = getUsersByRole('User');

  // Effect to load Growth Conversation data or reset form when dialog opens
  useEffect(() => {
    console.log('Editing Growth Conversation:', editingGrowthSession);
    console.log('editingGrowthSession status:', editingGrowthSession?.status);
    if (open) {
      if (editingGrowthSession) {
        if (editingGrowthSession.status === 'lead_assigned') {
          console.log('LEAD_ASSIGNED: Resetting form with basic info only');
          // For lead_assigned status, reset form but keep basic session info
          const formWithBasicInfo = {
            ...initialFormState,
            user_id: editingGrowthSession.user_id || '',
            lead_id: editingGrowthSession.lead_id || '',
            manager_id: editingGrowthSession.manager_id || '',
            date: formatDate(editingGrowthSession.date),
            review_period_from: formatDate(editingGrowthSession.review_period_from),
            review_period_to: formatDate(editingGrowthSession.review_period_to),
            session_data: {
              ...initialFormState.session_data,
              dynamic_fields: []
            }
          };
          console.log('Setting form with basic info:', formWithBasicInfo);
          setGrowthSessionForm(formWithBasicInfo);
        } else {
          // For other statuses, load existing session data
          const sessionData = typeof editingGrowthSession.session_data === 'string' ?
            JSON.parse(editingGrowthSession.session_data) : editingGrowthSession.session_data || {};
          setGrowthSessionForm({
            // ...editingGrowthSession,
            user_id: editingGrowthSession.user_id || '',
            lead_id: editingGrowthSession.lead_id || '',
            manager_id: editingGrowthSession.manager_id || '',
            date: formatDate(editingGrowthSession.date),
            review_period_from: formatDate(editingGrowthSession.review_period_from),
            review_period_to: formatDate(editingGrowthSession.review_period_to),
            session_data: {
              key_strengths: sessionData.key_strengths || '',
              areas_for_improvement: sessionData.areas_for_improvement || '',
              recommended_action_plan: sessionData.recommended_action_plan || '',
              support_required: sessionData.support_required || '',
              suggested_plan_of_action: sessionData.suggested_plan_of_action || '',
              dynamic_fields: Array.isArray(sessionData.dynamic_fields) ? sessionData.dynamic_fields : []
            },
            manager_comments: editingGrowthSession.manager_comments || ''
          });
        }
      } else {
        resetForm();
        if (selectedUserId && !forceNew) {
          const user = usersList.find(u => u.id === selectedUserId);

          // Look for existing lead_assigned in Growth Conversations
          const userGrowthSession = growthSessions.find(gs =>
            (gs.user_id === selectedUserId || gs.user_id === user?.email) &&
            (gs.status === 'lead_assigned')
          );

          if (user) {
            // Load existing dynamic fields from lead_assigned if available
            const existingSessionData = userGrowthSession?.session_data ? 
              (typeof userGrowthSession.session_data === 'string' ? 
                JSON.parse(userGrowthSession.session_data) : userGrowthSession.session_data) : {};
            
            setGrowthSessionForm(prev => ({
              ...prev,
              user_id: selectedUserId,
              // Use lead_id and manager_id from Growth Conversation if available, otherwise fall back to user profile
              lead_id: userGrowthSession?.lead_id || user.assignedLead || '',
              manager_id: userGrowthSession?.manager_id || user.assignedManager || '',
              session_data: {
                ...prev.session_data,
                dynamic_fields: Array.isArray(existingSessionData.dynamic_fields) ? existingSessionData.dynamic_fields : []
              }
            }));
          }
        } else if (selectedUserId && forceNew) {
          // For forced new sessions, just set the user_id and keep everything else clean
          setGrowthSessionForm(prev => ({
            ...prev,
            user_id: selectedUserId
          }));
        }
      }
      setFormErrors({});
    }
  }, [editingGrowthSession, selectedUserId, open, forceNew]);

  // Debug effect to track form changes
  useEffect(() => {
    if (open) {
      console.log('Final growthSessionForm state:', growthSessionForm);
    }
  }, [growthSessionForm, open]);

  const resetForm = () => {
    const formWithCustomFields = {
      ...initialFormState,
      session_data: {
        ...initialFormState.session_data,
        dynamic_fields: []
      }
    };
    console.log('Resetting form with dynamic fields:', formWithCustomFields);
    setGrowthSessionForm(formWithCustomFields);
    setFormErrors({});
    setActiveStep(0);
    setError('');
  };

  const handleFormChange = (field, value) => {
    if (field.startsWith('session_data.')) {
      const sessionField = field.split('.')[1];
      setGrowthSessionForm(prev => ({
        ...prev,
        session_data: {
          ...prev.session_data,
          [sessionField]: value
        }
      }));
    } else {
      setGrowthSessionForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
    setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Dynamic Fields Handlers
  const handleAddDynamicField = () => {
    const newField = {
      id: Date.now(),
      label: '',
      value: ''
    };
    setGrowthSessionForm(prev => {
      // Ensure session_data exists and dynamic_fields is an array
      const currentSessionData = prev.session_data || {};
      const currentDynamicFields = Array.isArray(currentSessionData.dynamic_fields) ? currentSessionData.dynamic_fields : [];
      
      return {
        ...prev,
        session_data: {
          ...currentSessionData,
          dynamic_fields: [...currentDynamicFields, newField]
        }
      };
    });
  };

  const handleDynamicFieldChange = (index, fieldType, value) => {
    setGrowthSessionForm(prev => {
      const currentSessionData = prev.session_data || {};
      const currentDynamicFields = Array.isArray(currentSessionData.dynamic_fields) ? currentSessionData.dynamic_fields : [];
      const updatedFields = [...currentDynamicFields];
      updatedFields[index] = {
        ...updatedFields[index],
        [fieldType]: value
      };
      return {
        ...prev,
        session_data: {
          ...currentSessionData,
          dynamic_fields: updatedFields
        }
      };
    });
  };

  const handleRemoveDynamicField = (index) => {
    setGrowthSessionForm(prev => {
      const currentSessionData = prev.session_data || {};
      const currentDynamicFields = Array.isArray(currentSessionData.dynamic_fields) ? currentSessionData.dynamic_fields : [];
      const updatedFields = [...currentDynamicFields];
      updatedFields.splice(index, 1);
      return {
        ...prev,
        session_data: {
          ...currentSessionData,
          dynamic_fields: updatedFields
        }
      };
    });
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Basic Information
        return growthSessionForm.user_id && growthSessionForm.date &&
          growthSessionForm.review_period_from && growthSessionForm.review_period_to;
      case 1: // Feedback Details
        return growthSessionForm.session_data.key_strengths.trim() !== '' &&
          growthSessionForm.session_data.areas_for_improvement.trim() !== '';
      case 2: // Review
        return true;
      default:
        return false;
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!growthSessionForm.user_id) {
      errors.user_id = 'User is required';
    }
    if (!growthSessionForm.date) {
      errors.date = 'Date is required';
    }
    if (!growthSessionForm.review_period_from) {
      errors.review_period_from = 'Review period start is required';
    }
    if (!growthSessionForm.review_period_to) {
      errors.review_period_to = 'Review period end is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // Form validation function for accordion mode
  const isFormValid = () => {
    // Check required fields for all steps
    const step0Valid = growthSessionForm.user_id && growthSessionForm.date &&
      growthSessionForm.review_period_from && growthSessionForm.review_period_to;
    
    const step1Valid = growthSessionForm.session_data.key_strengths.trim() !== '' &&
      growthSessionForm.session_data.areas_for_improvement.trim() !== '';
    
    // Step 2 is just review
    
    return step0Valid && step1Valid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const formDataToSave = {
        ...growthSessionForm,
        status: 'growth_session', // Update status to completed when feedback is added
        updated_at: new Date().toISOString()
      };

      if (editingGrowthSession) {
        // Always update existing Growth Conversation (whether it's an assignment or existing feedback)
        await updateGrowthSessionAPI({
          id: editingGrowthSession.id,
          user_id: growthSessionForm.user_id,
          session_data: growthSessionForm.session_data,
          manager_comments: growthSessionForm.manager_comments,
          status: formDataToSave.status,
          updated_at: formDataToSave.updated_at,
          review_period_from: growthSessionForm.review_period_from,
          review_period_to: growthSessionForm.review_period_to,
          date: growthSessionForm.date,
        });
        updateGrowthSession(editingGrowthSession.id, formDataToSave);
      } else {
        // This shouldn't happen in the new workflow, but keep for safety
        const newEntry = {
          ...formDataToSave,
          id: Date.now().toString(),
          created_at: new Date().toISOString(),
          created_by: localStorage.getItem("user")
        };
        await createGrowthSessionAPI(newEntry);
        addGrowthSession(newEntry);
      }
      
      // Call success handler if provided
      if (onSuccess) {
        onSuccess(editingGrowthSession ? 'updated' : 'created');
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving Growth Conversation:', error);
      setError('An error occurred while saving. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedUser = usersList.find(user => user.id === growthSessionForm.user_id);
  // const selectedLead = leadsList.find(lead => lead.id === growthSessionForm.lead_id);
  // const selectedManager = managersList.find(manager => manager.id === growthSessionForm.manager_id);
  console.log('growthSessionForm:', growthSessionForm);
  const getStepContent = (step) => {
    switch (step) {
      case 0: // Basic Information
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" icon={<PersonIcon />} sx={{ fontSize: '0.8rem' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  Basic Information
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  Set up the basic details for the Growth Conversation including user, dates, and assignments.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="User"
                fullWidth
                required
                value={growthSessionForm.user_id || ''}
                disabled
                error={!!formErrors.user_id}
                helperText={formErrors.user_id}
                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
                FormHelperTextProps={{ sx: { fontSize: '0.7rem' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Session Date"
                value={growthSessionForm.date}
                onChange={(e) => handleFormChange('date', e.target.value)}
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }}
                InputProps={{ sx: { fontSize: '0.875rem' } }}
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
                required
                error={!!formErrors.date}
                helperText={formErrors.date}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Review Period From"
                value={growthSessionForm.review_period_from}
                onChange={(e) => handleFormChange('review_period_from', e.target.value)}
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }}
                InputProps={{ sx: { fontSize: '0.875rem' } }}
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
                required
                error={!!formErrors.review_period_from}
                helperText={formErrors.review_period_from}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Review Period To"
                value={growthSessionForm.review_period_to}
                onChange={(e) => handleFormChange('review_period_to', e.target.value)}
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.875rem' } }}
                InputProps={{ sx: { fontSize: '0.875rem' } }}
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
                required
                error={!!formErrors.review_period_to}
                helperText={formErrors.review_period_to}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Assigned Lead"
                fullWidth
                disabled
                value={growthSessionForm.lead_id || ''}
                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Assigned Manager"
                fullWidth
                disabled
                value={growthSessionForm.manager_id || ''}
                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
              />
            </Grid>
          </Grid>
        );

      case 1: // Feedback Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="success" icon={<FeedbackIcon />} sx={{ fontSize: '0.8rem' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  Feedback Details
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  Provide detailed feedback including strengths, areas for improvement, and action plans.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Key Strengths"
                value={growthSessionForm.session_data.key_strengths}
                onChange={(e) => handleFormChange('session_data.key_strengths', e.target.value)}
                placeholder="Describe the employee's key strengths and achievements..."
                variant="outlined"
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                InputProps={{
                  sx: { fontSize: '0.875rem' }
                }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.8rem',
                    opacity: 0.6
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Areas for Improvement"
                value={growthSessionForm.session_data.areas_for_improvement}
                onChange={(e) => handleFormChange('session_data.areas_for_improvement', e.target.value)}
                placeholder="Identify areas where the employee can improve..."
                variant="outlined"
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                InputProps={{
                  sx: { fontSize: '0.875rem' }
                }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.8rem',
                    opacity: 0.6
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Recommended Action Plan"
                value={growthSessionForm.session_data.recommended_action_plan}
                onChange={(e) => handleFormChange('session_data.recommended_action_plan', e.target.value)}
                placeholder="Suggest specific actions for improvement..."
                variant="outlined"
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                InputProps={{
                  sx: { fontSize: '0.875rem' }
                }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.8rem',
                    opacity: 0.6
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Support Required"
                value={growthSessionForm.session_data.support_required}
                onChange={(e) => handleFormChange('session_data.support_required', e.target.value)}
                placeholder="What support does the employee need..."
                variant="outlined"
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                InputProps={{
                  sx: { fontSize: '0.875rem' }
                }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.8rem',
                    opacity: 0.6
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Suggested Plan of Action for Improvements"
                value={growthSessionForm.session_data.suggested_plan_of_action}
                onChange={(e) => handleFormChange('session_data.suggested_plan_of_action', e.target.value)}
                placeholder="Detailed plan for implementing improvements..."
                variant="outlined"
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                InputProps={{
                  sx: { fontSize: '0.875rem' }
                }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.8rem',
                    opacity: 0.6
                  }
                }}
              />
            </Grid>

            {/* Dynamic Fields Section */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'primary.main' }}>
                      Additional Details
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={handleAddDynamicField}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        borderColor: 'primary.main',
                        color: 'primary.main'
                      }}
                    >
                      Add Field
                    </Button>
                  </Box>
                  
                  {Array.isArray(growthSessionForm.session_data.dynamic_fields) && growthSessionForm.session_data.dynamic_fields.length > 0 ? (
                    <Stack spacing={2}>
                      {growthSessionForm.session_data.dynamic_fields.map((field, index) => (
                        <Box key={field.id || index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <TextField
                            label="Field Name"
                            value={field.label || ''}
                            onChange={(e) => handleDynamicFieldChange(index, 'label', e.target.value)}
                            size="small"
                            sx={{ minWidth: 150 }}
                            InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                            InputProps={{ sx: { fontSize: '0.8rem' } }}
                          />
                          <TextField
                            label="Value"
                            value={field.value || ''}
                            onChange={(e) => handleDynamicFieldChange(index, 'value', e.target.value)}
                            size="small"
                            fullWidth
                            InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                            InputProps={{ sx: { fontSize: '0.8rem' } }}
                          />
                          <IconButton
                            onClick={() => handleRemoveDynamicField(index)}
                            size="small"
                            color="error"
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                      No additional fields added yet. Click "Add Field" to add custom details.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 2: // Review & Finalize
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ fontSize: '0.875rem' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  Review & Finalize
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Review all information before saving the Growth Conversation.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Growth Conversation Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>User:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {growthSessionForm.user_id || 'Not selected'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Session Date:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>{growthSessionForm.date}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Review Period:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {growthSessionForm.review_period_from} to {growthSessionForm.review_period_to}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Assigned Lead:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {growthSessionForm.lead_id || 'Not assigned'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Key Strengths:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {growthSessionForm.session_data.key_strengths || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Areas for Improvement:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {growthSessionForm.session_data.areas_for_improvement || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Dynamic Fields:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {Array.isArray(growthSessionForm.session_data.dynamic_fields) && growthSessionForm.session_data.dynamic_fields.length > 0 ?
                          growthSessionForm.session_data.dynamic_fields.map(f => `${f.label}: ${f.value}`).join(', ') :
                          'None'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
        color: 'white',
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
          <FeedbackIcon sx={{ fontSize: 22, color: 'white' }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
            {editingGrowthSession ? 'Edit Growth Conversation' : 'Create Growth Conversation'}
          </Typography>
          {selectedUser && (
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
              {selectedUser.name} ({selectedUser.email})
            </Typography>
          )}
          {!editingGrowthSession && (
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', fontStyle: 'italic' }}>
              Multiple sessions per user supported across different review periods
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mx: 3, mt: 1, mb: 1, fontSize: '0.875rem' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {error}
            </Typography>
          </Alert>
        )}

        {loading && <LinearProgress sx={{ mb: 1 }} />}

        {showAccordions ? (
          <Box sx={{ p: 2 }}>
            {/* <Typography variant="h6" gutterBottom>
              Edit Mode - Accordion View
            </Typography> */}
            {steps.map((label, index) => (
              <Accordion 
                key={index}
                defaultExpanded={true}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                    {label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {getStepContent(index)}
                </AccordionDetails>
              </Accordion>
            ))}
            
            {/* Spacer to prevent content from being hidden behind sticky footer */}
            <Box sx={{ height: 80 }} />
          </Box>
        ) : (
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ p: 3 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>
                <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                  {label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ mt: 1, mb: 1 }}>
                  {getStepContent(index)}
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Button
                    variant="contained"
                    onClick={index === steps.length - 1 ? handleSave : handleNext}
                    disabled={!validateStep(index) || loading}
                    startIcon={index === steps.length - 1 ? <SaveIcon sx={{ fontSize: 18 }} /> : <FeedbackIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      mt: 1,
                      mr: 1,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      borderRadius: 2
                    }}
                  >
                    {loading ? 'Saving...' : index === steps.length - 1 ? 'Save Growth Conversation' : 'Next'}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{
                      mt: 1,
                      mr: 1,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    Back
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
          </Stepper>
        )}
      </DialogContent>

      {/* Sticky Action Buttons for Accordion Mode */}
      {showAccordions && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            bgcolor: 'white',
            borderTop: `1px solid ${theme.palette.divider}`,
            zIndex: 10,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 500,
                borderRadius: 2,
                minWidth: 80
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !isFormValid()}
              startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 120
              }}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Box>
          {!isFormValid() && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center', fontSize: '0.7rem' }}>
              Please fill in all required fields to save
            </Typography>
          )}
        </Box>
      )}

      {!showAccordions && (
        <DialogActions sx={{ p: 3, bgcolor: 'rgba(0,0,0,0.02)' }}>
          <Button
            onClick={onClose}
            color="secondary"
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              minWidth: 100
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GrowthSessionDialog;